import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Callout,
  DirectionalHint,
  Spinner,
  SpinnerSize,
  mergeStyleSets,
} from "@fluentui/react";
import {
  MicrosoftExcelLogo,
  Code,
  Database,
  Warning,
  Broom,
  CaretLeft,
  CaretRight,
  DotsThreeVertical,
  PencilSimple,
  Trash,
  ChartBar,
  Table as TableIcon,
  ShieldCheck,
  Lock,
} from "@phosphor-icons/react";
import { useAuth } from "../hooks/useAuth";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useSearchParams } from "react-router-dom";

import { agentService } from "../services/agentService";
import type { Agent, AgentTrace, AgentTraceStep, Message } from "../types/agent.types";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { exportTableToExcel, containsMarkdownTable } from "../utils/exportTableToExcel";
import ChartPanel from "../components/chat/ChartPanel";
import DataTable from "../components/chat/DataTable";
import WorkspacePanel, { WorkspaceWidget } from "../components/chat/WorkspacePanel";
import { dashboardBuilderService } from "../services/dashboardBuilderService";
import { datasetService } from "../services/datasetService";
import type { DepartmentSummary } from "../types/dataset.types";
import type { DashboardWidget } from "../types/dashboard.types";
import { useNavigate } from "react-router-dom";
import WelcomeScreen from "../components/chat/WelcomeScreen";
import InputBar from "../components/chat/InputBar";
import WorkspaceSidebar from "../components/chat/WorkspaceSidebar";
import { FeedbackBar } from "../components/chat/FeedbackBar";
import { feedbackService } from "../services/feedbackService";
import type { FeedbackRating } from "../services/feedbackService";
import { accessService } from "../services/accessService";
import type { WorkspaceSession } from "../components/chat/WorkspaceSidebar";
import type { AttachedFile, DatasetContextInfo } from "../components/chat/ContextChips";
import {
  loadSessions,
  loadMessages,
  saveMessages,
  deleteSession as deleteSessionStorage,
  upsertSession,
  generateTitle,
} from "../utils/workspaceStorage";

/* =========================
   PR 4a — KPI card detection + value formatting helpers.
   Pure functions so they can be unit-tested cleanly later.
========================= */

/** Decide whether a response should render as a KPI card grid instead of
 *  a 1-row markdown table. Signature kept narrow on purpose — only reads
 *  what's needed off the message. */
function isKpiCardResponse(
  chartData: { rowCount: number; rows: Record<string, any>[]; fields: { name: string; dataType: string | number }[] } | null | undefined,
  content: string,
): boolean {
  if (!chartData) return false;
  if (chartData.rowCount !== 1) return false;
  if (!Array.isArray(chartData.rows) || chartData.rows.length !== 1) return false;
  if (!Array.isArray(chartData.fields) || chartData.fields.length === 0) return false;
  // Cap KPI count — more than 6 is overload, fall back to table.
  if (chartData.fields.length > 6) return false;
  // If the prose has no markdown table, the inline-answer formatter has
  // already produced a clean rendering. Don't double up with KPI cards.
  if (!/\|\s+/.test(content)) return false;

  const row = chartData.rows[0];
  for (const field of chartData.fields) {
    // Identifier-shaped column names (CostCentreNumber, AccountId, FiscalYear)
    // disqualify — those are categorical even if their value is numeric.
    if (/(?:number|code|num|key|id|name|year|type)$/i.test(field.name)) {
      return false;
    }
    const v = row[field.name];
    if (v === null || v === undefined) continue;
    if (typeof v === "number") continue;
    if (typeof v === "string") {
      const trimmed = v.trim();
      if (!trimmed) continue;
      // Accept: $1,234, -$1,234.56, 89.8%, -89.8%, plain numbers, comma-separated
      if (/^-?\$?[\d,]+(\.\d+)?%?$/.test(trimmed)) continue;
      return false;
    }
    return false;
  }
  return true;
}

/** Parse a value that might be a number, currency string, or percentage. */
function parseNumericValue(raw: any): { num: number; isCurrency: boolean; isPercent: boolean } | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") {
    return { num: raw, isCurrency: false, isPercent: false };
  }
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const isPercent = trimmed.endsWith("%");
  const isCurrency = trimmed.includes("$");
  const isNegative = trimmed.startsWith("-");
  const numStr = trimmed.replace(/[$%,]/g, "").replace(/^-/, "");
  const num = parseFloat(numStr);
  if (isNaN(num)) return null;
  return { num: isNegative ? -num : num, isCurrency, isPercent };
}

/** Format a KPI value: abbreviate large numbers, preserve sign + currency + percent. */
function formatKpiValue(raw: any): string {
  const parsed = parseNumericValue(raw);
  if (!parsed) return raw == null ? "—" : String(raw);
  const { num, isCurrency, isPercent } = parsed;

  if (isPercent) {
    return `${num.toFixed(1)}%`;
  }

  const abs = Math.abs(num);
  let body: string;
  if (abs >= 1_000_000_000) {
    body = `${(abs / 1_000_000_000).toFixed(1)}B`;
  } else if (abs >= 1_000_000) {
    body = `${(abs / 1_000_000).toFixed(1)}M`;
  } else if (abs >= 10_000) {
    body = `${(abs / 1_000).toFixed(1)}k`;
  } else if (abs >= 1) {
    body = abs.toLocaleString("en-AU", { maximumFractionDigits: 0 });
  } else {
    body = abs.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  const sign = num < 0 ? "-" : "";
  const symbol = isCurrency ? "$" : "";
  return `${sign}${symbol}${body}`;
}

/** Convert a SQL column name to a readable header.
 *  "YTDActuals" → "YTD Actuals", "variance_amount" → "Variance Amount". */
function formatColumnHeader(name: string): string {
  let clean = name.replace(/^\[|\]$/g, "").trim();
  if (/\s/.test(clean)) return clean;
  // Split between consecutive uppercase + lowercase: "YTDActuals" → "YTD Actuals"
  clean = clean.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");
  // Split camelCase / PascalCase: insert space before uppercase
  clean = clean.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  // Split on underscores
  clean = clean.replace(/_/g, " ");
  // Title case
  return clean.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** PR 8b — convert workspace widgets to the DashboardWidget shape the
 *  existing Dashboard Builder backend expects. Chart widgets map 1:1;
 *  KPI workspace widgets (a row of N numeric values) split into N
 *  individual dashboard KPI widgets so the saved dashboard renders
 *  the same way the panel does.
 *
 *  Grid sizes mirror DashboardBuilder.tsx conventions:
 *    - chart: size='large', gridW=12, gridH=4 (full-width row)
 *    - kpi:   size='small', gridW=4,  gridH=2 (3 per row)
 */
function workspaceToDashboardWidgets(widgets: WorkspaceWidget[]): DashboardWidget[] {
  const out: DashboardWidget[] = [];
  let gridY = 0;
  let position = 0;
  for (const w of widgets) {
    if (w.kind === "chart" && w.chartType) {
      out.push({
        id: w.id,
        type: "chart",
        chartType: w.chartType,
        chartData: w.data,
        title: w.caption,
        position: position++,
        size: "large",
        gridX: 0,
        gridY,
        gridW: 12,
        gridH: 4,
      });
      gridY += 4;
    } else if (w.kind === "kpi") {
      const row = w.data.rows?.[0];
      if (!row) continue;
      const fields = w.data.fields || [];
      let gridX = 0;
      let advanced = false;
      for (const field of fields) {
        const value = row[field.name];
        if (value == null) continue;
        // 3 KPIs per row (3 × gridW:4 = 12). Wrap to the next row when full.
        if (gridX >= 12) {
          gridX = 0;
          gridY += 2;
        }
        out.push({
          id: `${w.id}-${field.name}`,
          type: "kpi",
          title: formatColumnHeader(field.name),
          kpiLabel: formatColumnHeader(field.name),
          kpiValue: String(value),
          position: position++,
          size: "small",
          gridX,
          gridY,
          gridW: 4,
          gridH: 2,
        });
        gridX += 4;
        advanced = true;
      }
      if (advanced) gridY += 2;
    }
  }
  return out;
}

/** PR 8 — derive a short caption for a workspace widget from the user's
 *  question + the assistant's first prose line. Used as the widget head
 *  label so the user can scan the panel and remember which question
 *  produced which visual. */
function captionFor(userQuery: string, responseContent: string): string {
  // Prefer the user's question — short and intent-shaped. Fall back to
  // the first non-empty line of the assistant's prose.
  const q = (userQuery || "").trim();
  if (q && q.length <= 80) return q.replace(/[?.!]+$/, "");
  if (q) return q.slice(0, 77).replace(/[?.!]+$/, "") + "…";
  const firstLine = (responseContent || "")
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.length > 0 && !l.startsWith("|") && !l.startsWith("**"));
  if (firstLine) {
    return firstLine.length <= 80 ? firstLine : firstLine.slice(0, 77) + "…";
  }
  return "Result";
}

/** When KPI cards take over for a single-row response, strip the
 *  "**N row(s) returned.**" line and the markdown table block from the
 *  prose so they don't double-up with the cards.
 *  Note: row\(?s?\)? matches all of "row", "rows", and "row(s)" — the
 *  formatter uses the parenthesised form. */
function stripCountAndTableFromMarkdown(content: string): string {
  return content
    .replace(/(?:^|\n)\*\*\d+\s+row\(?s?\)?\s+returned\.\*\*[^\n]*/g, "")
    .replace(/(?:^|\n)\*\*Showing\s+\d+\s+of\s+\d+\s+row\(?s?\)?\.\*\*[^\n]*/g, "")
    .replace(/(?:^|\n)\|[^\n]*\|/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Decide whether a column is "numeric-shaped" by sampling values from
 *  the first few rows. Identifier-shaped column NAMES disqualify even
 *  when their values are numbers (CostCentreNumber etc). */
function isNumericField(
  field: { name: string; dataType: string | number },
  rows: Record<string, any>[],
): boolean {
  if (/(?:number|code|num|key|id|year|type)$/i.test(field.name)) return false;
  for (const row of rows.slice(0, 5)) {
    const v = row[field.name];
    if (v === null || v === undefined) continue;
    if (typeof v === "number") return true;
    if (typeof v === "string") {
      const trimmed = v.trim();
      if (!trimmed) continue;
      if (/^-?\$?[\d,]+(\.\d+)?%?$/.test(trimmed)) return true;
      return false;
    }
    return false;
  }
  return false;
}

/** Auto-pick a default chart type for a multi-row response (PR 4b).
 *  Returns null when the response shape doesn't lend itself to a chart
 *  (e.g. too many rows, no obvious category vs metric split, single
 *  row — KPI cards handle that case). */
function pickAutoChartType(
  chartData: { rowCount: number; rows: Record<string, any>[]; fields: { name: string; dataType: string | number }[] } | null | undefined,
): "bar" | "pie" | "line" | null {
  if (!chartData) return null;
  if (chartData.rowCount < 2) return null; // single row → KPI cards
  if (chartData.rowCount > 100) return null; // too many for a chart
  if (!chartData.fields || chartData.fields.length === 0) return null;
  if (!chartData.rows || chartData.rows.length === 0) return null;

  // Identify a date/period column by name. Common patterns: PeriodName,
  // FiscalPeriod, Month, Year, Date, Quarter, FYxx etc.
  const dateOrPeriodCol = chartData.fields.find((f) =>
    /^(?:date|period|month|year|fiscal|quarter)|(?:date|period|month|fiscal|quarter)$/i.test(f.name),
  );

  const numericFields = chartData.fields.filter((f) => isNumericField(f, chartData.rows));
  const nonNumericFields = chartData.fields.filter((f) => !numericFields.includes(f));

  // Time series → line chart
  if (dateOrPeriodCol && numericFields.length >= 1) return "line";

  // Category-by-numeric breakdown → bar chart. Allows 1-2 categorical
  // columns (e.g. [Code, Name, Value] is still a ranking — Name acts as
  // the chart label, Code is supplementary context) so common Finance /
  // Assets queries like "top 10 accounts by GL spend" auto-chart.
  if (
    nonNumericFields.length >= 1 &&
    nonNumericFields.length <= 2 &&
    numericFields.length >= 1 &&
    chartData.rowCount <= 20
  ) {
    return "bar";
  }

  // Otherwise: no auto-chart. The DataTable fallback (see UnifiedChat
  // assistant rendering) will render the polished table instead of the
  // raw markdown one, so the visual is still consistent.
  return null;
}

/* =========================
   Avatar helpers (PR 7) — circular, persona-aware identity marks.
   Replaces the rounded-square Robot/User icon avatars with branded
   initials so each persona has a distinct visual identity at a glance.
========================= */

/** Resolve a persona ID → { initial letter, gradient background, tier color
 *  for the persona's identity mark }. Falls back to a neutral grey for null
 *  agent (generic LLM) or unknown agents. */
function personaVisual(agent: Agent | null | undefined): { initial: string; gradient: string } {
  const id = agent?.id;
  switch (id) {
    case "finance":
      return { initial: "F", gradient: "linear-gradient(140deg, #1f6b2c, #2e873d)" };
    case "assets":
      return { initial: "A", gradient: "linear-gradient(140deg, #2e873d, #55a655)" };
    case "abs":
      return { initial: "B", gradient: "linear-gradient(140deg, #2563eb, #4f8ff0)" };
    default:
      // Use first letter of the agent name if available, else generic 'G'.
      if (agent?.name) {
        return { initial: agent.name.trim().charAt(0).toUpperCase() || "G", gradient: "linear-gradient(140deg, #2e873d, #55a655)" };
      }
      return { initial: "G", gradient: "linear-gradient(140deg, #4b5563, #6b7280)" };
  }
}

/** Initials from a display name or email. "Shri Wangnek" → "SW",
 *  "shridhar.wangnekar@..." → "SW", single names → first letter. */
function deriveUserInitials(name?: string | null, email?: string | null): string {
  const cleaned = (name || "").trim();
  if (cleaned) {
    const parts = cleaned.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  const local = (email || "").split("@")[0] || "";
  if (!local) return "U";
  const segs = local.split(/[._-]/).filter(Boolean);
  if (segs.length >= 2) return (segs[0][0] + segs[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

/* =========================
   Styles
========================= */
const styles = mergeStyleSets({
  pageRow: {
    height: "calc(100vh - 52px)",
    display: "flex",
    flexDirection: "row",
    color: boroondaraPalette.text,
    background: boroondaraPalette.bg,
    overflow: "hidden",
  },
  chatArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minWidth: 0,
    background: "#FAFAFA",
    transition: "flex-basis 0.22s ease",
  },
  // PR 8 — when the workspace panel is open, chat shares 50/50 with the
  // panel. The sidebar auto-collapses to give both panels breathing room.
  chatAreaSplit: {
    flex: "1 1 50%",
  },
  // PR 8 — workspace panel container. Sits to the right of the chat area
  // with a hairline separator. Animates open/closed via flex-basis.
  workspaceArea: {
    display: "flex",
    flexShrink: 0,
    overflow: "hidden",
    background: "#FAFAFA",
    borderLeft: "1px solid #E5E7EB",
    transition: "flex-basis 0.22s ease, flex-grow 0.22s ease, border-left-width 0.22s ease",
  } as any,
  workspaceAreaOpen: {
    flex: "1 1 50%",
  },
  workspaceAreaClosed: {
    flex: "0 0 0",
    borderLeftWidth: 0,
  },
  messagesScroll: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
  },
  messagesInner: {
    maxWidth: 820,
    width: "100%",
    margin: "0 auto",
    padding: "24px 24px 8px",
    // Flex column with min-height so the WelcomeScreen (flex: 1) can expand
    // to fill the scroll area and centre its content vertically. Conversation
    // messages flow normally as flex children.
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    boxSizing: "border-box",
    "@media (max-width: 768px)": {
      padding: "16px 12px 8px",
    },
  } as any,

  /* Conversation bar (PR 2a) — header above the messages.
     Carries the conversation title (editable), persona chip + scope,
     and right-side actions (New chat + ⋯ menu for Rename/Delete). */
  convBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "8px 16px",
    borderBottom: "1px solid #EDEDED",
    background: "#FAFAFA",
    minHeight: 44,
  },
  convBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  convBarSidebarToggle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 10px",
    borderRadius: 6,
    border: "1px solid #E1E1E1",
    background: "#fff",
    color: "#666",
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
    flexShrink: 0,
    transition: "all 0.15s",
    selectors: {
      ":hover": { background: "#F5F5F5", color: "#232323" },
    },
  },
  convBarTitleBlock: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },
  convBarTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: boroondaraPalette.text,
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: 4,
    padding: "2px 6px",
    margin: "-2px -6px",
    cursor: "text",
    fontFamily: "inherit",
    textAlign: "left" as const,
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    transition: "border-color 0.12s, background 0.12s",
    selectors: {
      ":hover": { borderColor: "#E1E1E1", background: "#fff" },
      ":focus-visible": {
        outline: "none",
        borderColor: boroondaraPalette.primary,
        background: "#fff",
      },
    },
  } as any,
  convBarTitleInput: {
    fontSize: 14,
    fontWeight: 600,
    color: boroondaraPalette.text,
    background: "#fff",
    border: `1px solid ${boroondaraPalette.primary}`,
    borderRadius: 4,
    padding: "2px 6px",
    margin: "-3px -7px",
    fontFamily: "inherit",
    width: "100%",
    minWidth: 0,
    outline: "none",
    boxShadow: `0 0 0 3px rgba(46,135,61,0.10)`,
  },
  convBarSubLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 2,
    fontSize: 11.5,
    color: boroondaraPalette.text3,
    flexWrap: "wrap",
  } as any,
  convBarPersonaChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "1px 8px",
    borderRadius: 999,
    background: "#f3fcf3",
    border: "1px solid #c9eccc",
    color: boroondaraPalette.primary,
    fontSize: 11,
    fontWeight: 500,
  },
  convBarPersonaDot: {
    width: 5,
    height: 5,
    background: boroondaraPalette.primary,
    borderRadius: 50,
    flexShrink: 0,
  },
  convBarScope: {
    color: "#9C6B00",
    background: "#fbf3df",
    border: "1px solid #f0e0b8",
    padding: "1px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 500,
  },
  // Data classification badge — same chip language as persona/scope.
  // Public (PostgreSQL-backed) reads green; governed (Fabric, OFFICIAL:
  // Sensitive) reads amber.
  convBarClassPublic: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "1px 8px",
    borderRadius: 999,
    background: "#f3fcf3",
    border: "1px solid #c9eccc",
    color: boroondaraPalette.primary,
    fontSize: 11,
    fontWeight: 500,
  } as any,
  convBarClassGov: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "1px 8px",
    borderRadius: 999,
    background: "#fbf3df",
    border: "1px solid #f0e0b8",
    color: "#9C6B00",
    fontSize: 11,
    fontWeight: 500,
  } as any,
  convBarRight: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  convBarPrimaryBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    borderRadius: 6,
    border: "1px solid #D1D5DB",
    background: "#fff",
    color: boroondaraPalette.primary,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.15s",
    selectors: {
      ":hover": {
        borderColor: boroondaraPalette.primary,
        background: "#f3fcf3",
      },
    },
  },
  convBarIconBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    borderRadius: 6,
    border: "1px solid #E1E1E1",
    background: "#fff",
    color: "#666",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.15s",
    selectors: {
      ":hover": { borderColor: "#9CA3AF", color: "#232323" },
    },
  },
  convBarMenuItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    cursor: "pointer",
    color: boroondaraPalette.text,
    fontSize: 13,
    fontWeight: 500,
    transition: "background 0.12s",
    selectors: {
      ":hover": { background: "#F5F5F5" },
    },
  },
  convBarMenuItemDanger: {
    color: "#B42318",
    selectors: {
      ":hover": { background: "#fde8e6" },
    },
  },

  /* Messages */
  messageRow: {
    display: "flex",
    gap: 16,
    marginBottom: 32,
    alignItems: "flex-start",
  },
  messageRowUser: {
    flexDirection: "row-reverse",
  },
  // PR 7 — circular, persona-aware identity marks. The assistant avatar's
  // background gradient comes from personaVisual() at render time; styles
  // here cover the shared geometry + typography.
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.01em",
  },
  avatarAssistant: {
    // background is set inline via the persona's gradient
    color: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.10), inset 0 -1px 0 rgba(0,0,0,0.06)",
  },
  avatarUser: {
    background: "#f3fcf3",
    border: "1px solid #c9eccc",
    color: boroondaraPalette.primary,
  },
  messageContent: {
    flex: 1,
    minWidth: 0,
    maxWidth: 720,
  },
  userBubble: {
    background: "linear-gradient(135deg, #e8f5e9, #f1f8e9)",
    padding: "12px 18px",
    borderRadius: "18px 18px 4px 18px",
    color: "#232323",
    fontSize: 14,
    lineHeight: "1.65",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    width: "fit-content",
    maxWidth: "100%",
    marginLeft: "auto",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  // Brand-green left rail marks the prose as the assistant's turn — pairs
  // with the green avatar on the same row. The 1px gray border continues
  // on the other 3 sides; padding-left trimmed by 2px to compensate for
  // the thicker left border so the text alignment stays put.
  assistantProse: {
    color: "#333333",
    fontSize: 14,
    lineHeight: "1.75",
    background: "#FFFFFF",
    borderRadius: 12,
    padding: "16px 20px 16px 18px",
    border: "1px solid #EDEDED",
    borderLeft: "3px solid #2e873d",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  systemBubble: {
    background: "#FFF8E1",
    border: "1px solid #FFE082",
    padding: "12px 18px",
    borderRadius: 12,
    color: "#8D6E00",
    fontSize: 14,
    lineHeight: "1.65",
  },
  timeText: {
    fontSize: 11,
    color: "#747474",
    marginTop: 6,
    display: "block",
  },
  timeRight: { textAlign: "right" },

  /* KPI cards (PR 4a) — auto-render single-row summary responses as a
     compact card grid instead of a 4-column markdown table. */
  kpiRow: {
    marginTop: 14,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 12,
  },
  kpiCard: {
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: "14px 16px",
  },
  kpiLabel: {
    fontSize: 11,
    color: "#747474",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    fontWeight: 600,
    marginBottom: 4,
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 600,
    lineHeight: "1.1",
    color: "#232323",
    letterSpacing: "-0.01em",
    fontVariantNumeric: "tabular-nums",
  } as any,
  kpiValueBad: { color: "#B42318" },
  kpiValueGood: { color: "#2e873d" },

  /* Citations */
  citationsWrap: {
    marginTop: 14,
    paddingTop: 12,
    borderTop: "1px solid #EDEDED",
  },
  citationsTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: "#747474",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 8,
  },
  citationChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 10px",
    borderRadius: 8,
    background: "#F5F5F5",
    border: "1px solid #EDEDED",
    fontSize: 12,
    color: "#484848",
    marginRight: 8,
    marginBottom: 6,
  },

  /* Export + chart buttons */
  exportBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    borderRadius: 8,
    border: "1px solid #E1E1E1",
    background: "#FFFFFF",
    color: "#484848",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    marginTop: 10,
    transition: "all 0.15s ease",
    selectors: {
      ":hover": {
        background: "#d9f4d7",
        borderColor: "#2e873d",
        color: "#2e873d",
      },
    },
  },
  /* Chart ↔ Table segmented control (PR 4c).
     Sits at the top-right of the chart/table area inside the prose. */
  viewSwitcherWrap: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 12,
  },
  viewSwitcher: {
    display: "inline-flex",
    background: "#f8f9fa",
    border: "1px solid #E5E7EB",
    borderRadius: 7,
    padding: 2,
    gap: 2,
  },
  viewSwitcherBtn: {
    background: "transparent",
    border: 0,
    padding: "4px 10px",
    fontSize: 11.5,
    color: "#747474",
    borderRadius: 5,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontFamily: "inherit",
    fontWeight: 500,
    transition: "background 0.12s, color 0.12s",
    selectors: {
      ":hover": { color: "#232323" },
    },
  } as any,
  viewSwitcherBtnActive: {
    background: "#FFFFFF",
    color: boroondaraPalette.primary,
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
  },

  /* Follow-up suggestions (PR 2c) — chips under the last assistant message.
     Encourages the next conversational move; stays always visible (not
     hover-revealed) since it's an invitation, not an action. */
  followupsBlock: {
    marginTop: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  followupsHeading: {
    fontSize: 11,
    fontWeight: 600,
    color: "#747474",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  followupsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  } as any,
  followupChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "7px 14px",
    borderRadius: 999,
    border: "1px solid #E1E1E1",
    background: "#FFFFFF",
    color: "#484848",
    fontSize: 12.5,
    fontFamily: "inherit",
    cursor: "pointer",
    textAlign: "left" as const,
    lineHeight: "1.4",
    transition: "border-color 0.12s, background 0.12s, color 0.12s",
    selectors: {
      ":hover": {
        borderColor: boroondaraPalette.primary,
        background: "#f3fcf3",
        color: boroondaraPalette.text,
      },
      ":hover .followup-arrow": {
        color: boroondaraPalette.primary,
      },
      ":focus-visible": {
        outline: `2px solid ${boroondaraPalette.primary}`,
        outlineOffset: 2,
      },
      ":disabled": {
        opacity: 0.55,
        cursor: "default",
      },
    },
  } as any,
  followupArrow: {
    color: "#9CA3AF",
    fontSize: 13,
    lineHeight: 1,
    transition: "color 0.12s",
  },

  /* Methodology drawer (PR 3b — refactored from App.css to mergeStyleSets,
     2026-05-10. The plain-CSS version did not apply correctly in the
     deployed build; switching to Fluent's mergeStyleSets matches the
     project pattern and resolves the styling). */
  methodology: {
    marginTop: 10,
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    overflow: "hidden",
    background: "#FFFFFF",
    fontFamily: "inherit",
  },
  methodologySummary: {
    listStyle: "none",
    cursor: "pointer",
    padding: "9px 14px",
    background: "#f8f9fa",
    fontSize: 12,
    color: "#484848",
    userSelect: "none",
    transition: "background 0.12s",
    display: "block", // override default summary list-item rendering
    selectors: {
      ":hover": { background: "#f0f0f0" },
      "::-webkit-details-marker": { display: "none" },
      "::marker": { display: "none", content: "''" },
    },
  } as any,
  // Inner flex row inside <summary> — flex on <summary> itself can be flaky
  // across browsers so we rely on a child wrapper.
  methodologySummaryRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
  } as any,
  methodologyChevron: {
    fontSize: 10,
    color: "#747474",
    flexShrink: 0,
    transition: "transform 0.15s",
  },
  methodologyChevronOpen: {
    transform: "rotate(90deg)",
  },
  methodologyHeadLabel: { fontWeight: 600 },
  methodologyHeadMeta: {
    marginLeft: "auto",
    fontSize: 10.5,
    color: "#747474",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontFamily: "'Roboto Mono', ui-monospace, Menlo, monospace",
  } as any,
  methodologyPill: {
    background: "#f3fcf3",
    color: boroondaraPalette.primary,
    border: "1px solid #c9eccc",
    padding: "1px 7px",
    borderRadius: 999,
    fontFamily: "inherit",
    fontSize: 10.5,
    fontWeight: 500,
  },
  methodologyPillWarn: {
    background: "#fbf3df",
    color: "#9C6B00",
    borderColor: "#f0e0b8",
  },
  methodologyBody: {
    padding: "12px 14px 14px",
    background: "#FFFFFF",
    borderTop: "1px solid #E5E7EB",
  },
  methodologyStep: {
    display: "grid",
    gridTemplateColumns: "26px 1fr",
    gap: 10,
    padding: "8px 0",
    fontSize: 12.5,
    lineHeight: "1.5",
    color: "#484848",
    selectors: {
      "& + &": { borderTop: "1px dashed #E5E7EB" },
    },
  } as any,
  methodologyStepRetry: {
    color: "#232323",
  },
  methodologyStepN: {
    fontFamily: "'Roboto Mono', ui-monospace, Menlo, monospace",
    fontSize: 11,
    color: "#9CA3AF",
    lineHeight: "1.5",
    textAlign: "right" as const,
  },
  methodologyStepWhat: {
    fontSize: 12.5,
    lineHeight: "1.5",
    display: "flex",
    alignItems: "baseline",
    flexWrap: "wrap",
    gap: 6,
  } as any,
  methodologyStepLabel: { fontWeight: 500, color: "#232323" },
  methodologyStepCode: {
    background: "#f8f9fa",
    border: "1px solid #E5E7EB",
    padding: "1px 6px",
    borderRadius: 4,
    fontFamily: "'Roboto Mono', ui-monospace, Menlo, monospace",
    fontSize: 11.5,
    color: "#232323",
  },
  // Same chip as methodologyStepCode, but it deep-links to the source
  // dataset. Brand-green on hover with a ↗ to signal navigation.
  methodologyStepCodeLink: {
    background: "#f8f9fa",
    border: "1px solid #E5E7EB",
    padding: "1px 6px",
    borderRadius: 4,
    fontFamily: "'Roboto Mono', ui-monospace, Menlo, monospace",
    fontSize: 11.5,
    color: "#232323",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    cursor: "pointer",
    fontWeight: 400,
    transition: "border-color 0.12s, color 0.12s, background 0.12s",
    selectors: {
      ":hover": { borderColor: boroondaraPalette.primary, color: boroondaraPalette.primary, background: "#f3fcf3" },
      ":hover .ds-link-arrow": { opacity: 1 },
      ":focus-visible": { outline: `2px solid ${boroondaraPalette.primary}`, outlineOffset: 1 },
    },
  } as any,
  methodologyStepCodeArrow: {
    fontSize: 9.5,
    opacity: 0.45,
    transition: "opacity 0.12s",
  },
  methodologyStepEm: {
    fontStyle: "normal" as const,
    color: "#747474",
    fontSize: 11.5,
  },
  methodologyStepBadge: {
    fontFamily: "'Roboto Mono', ui-monospace, Menlo, monospace",
    fontSize: 10.5,
    color: "#9CA3AF",
  },
  methodologyStepError: {
    color: "#B42318",
    fontSize: 11.5,
  },
  methodologyRetryMark: {
    color: "#9C6B00",
    fontWeight: 600,
    marginRight: 2,
  },
  methodologySql: {
    marginTop: 12,
    border: "1px solid #E5E7EB",
    borderRadius: 6,
    background: "#f8f9fa",
    overflow: "hidden",
  },
  methodologySqlSummary: {
    listStyle: "none",
    cursor: "pointer",
    padding: "7px 12px",
    fontSize: 11.5,
    color: "#484848",
    userSelect: "none",
    background: "#f8f9fa",
    transition: "background 0.12s",
    display: "block",
    selectors: {
      ":hover": { background: "#f0f0f0" },
      "::-webkit-details-marker": { display: "none" },
      "::marker": { display: "none", content: "''" },
    },
  } as any,
  methodologySqlSummaryRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  methodologySqlBody: {
    margin: 0,
    padding: "10px 14px",
    background: "#1f2330",
    color: "#d6e2cf",
    fontFamily: "'Roboto Mono', ui-monospace, Menlo, monospace",
    fontSize: 11.5,
    lineHeight: "1.55",
    overflowX: "auto",
    whiteSpace: "pre",
    borderTop: "1px solid #E5E7EB",
  } as any,

  /* Thinking — step-by-step status */
  thinkingRow: {
    display: "flex",
    gap: 16,
    marginBottom: 32,
    alignItems: "flex-start",
  },
  thinkingBubble: {
    padding: "16px 20px",
    borderRadius: 12,
    background: "#FFFFFF",
    border: "1px solid #EDEDED",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    minWidth: 0,
  },
  stepList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  stepItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 13,
  },
  stepDone: {
    color: "#107C10",
  },
  stepActive: {
    color: "#232323",
  },
  stepPending: {
    color: "#C8C8C8",
  },
  stepIcon: {
    width: 18,
    height: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  /* Loading */
  loadingPage: {
    height: "calc(100vh - 48px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
});

/* =========================
   Helpers
========================= */
const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const formatTime = (t: any) => {
  const d = t instanceof Date ? t : new Date(t);
  return d.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
};

const isNearBottom = (el: HTMLDivElement | null, threshold = 160) => {
  if (!el) return true;
  const { scrollTop, scrollHeight, clientHeight } = el;
  return scrollHeight - (scrollTop + clientHeight) < threshold;
};

type NormalizedCitation = { label: string; url?: string };
const normalizeCitations = (raw: any): NormalizedCitation[] => {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr
    .map((c: any) => {
      if (!c) return null;
      if (c.id === "sql-query") return null; // handled separately
      if (typeof c === "string") {
        const isUrl = /^https?:\/\//i.test(c);
        return { label: isUrl ? "Source" : c, url: isUrl ? c : undefined };
      }
      if (typeof c === "object") {
        const label = c.title || c.label || c.name || c.source || c.document || c.url || c.href || "Source";
        const url = c.url || c.href || c.link;
        return { label: typeof label === "string" ? label : "Source", url: typeof url === "string" ? url : undefined };
      }
      return null;
    })
    .filter(Boolean) as NormalizedCitation[];
};

/* Thinking steps — single-line rotating status text */
const AGENT_STEPS = [
  "Interpreting your question...",
  "Building the SQL query...",
  "Pulling data from the warehouse...",
  "Joining tables and enriching results...",
  "Crunching the numbers...",
  "Formatting your answer...",
];
const GENERAL_STEPS = [
  "Reading your file...",
  "Analysing the content...",
  "Preparing your answer...",
];

/* Markdown components */
const markdownComponents: Record<string, React.FC<any>> = {
  table: ({ children, ...props }: any) => (
    <div className="md-table-wrap">
      <table className="md-table" {...props}>{children}</table>
    </div>
  ),
  code: ({ inline, className, children, ...props }: any) => {
    if (inline) return <code className="md-inline-code" {...props}>{children}</code>;
    return (
      <div className="md-code-block">
        <pre><code className={className} {...props}>{children}</code></pre>
      </div>
    );
  },
  p: ({ children, ...props }: any) => <p className="md-paragraph" {...props}>{children}</p>,
  strong: ({ children, ...props }: any) => <strong className="md-strong" {...props}>{children}</strong>,
  h1: ({ children, ...props }: any) => <h3 className="md-heading" {...props}>{children}</h3>,
  h2: ({ children, ...props }: any) => <h3 className="md-heading" {...props}>{children}</h3>,
  h3: ({ children, ...props }: any) => <h4 className="md-heading" {...props}>{children}</h4>,
};

/* =========================
   Component
========================= */
export default function UnifiedChat() {
  const [searchParams, setSearchParams] = useSearchParams();
  // PR 8b — used to navigate to the saved dashboard after Save as dashboard.
  const nav = useNavigate();

  // PR 7 — user identity for the user-message avatar initials.
  const { user } = useAuth();
  const userInitials = useMemo(
    () => deriveUserInitials(user?.name, user?.email),
    [user?.name, user?.email],
  );

  /* --- agents --- */
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  /* --- finance scope summary (for the welcome hint when Finance is picked) --- */
  const [financeScopeSummary, setFinanceScopeSummary] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    accessService
      .get<{ financeScope?: { mode: string; summary?: string } }>("/me")
      .then((r) => {
        if (!alive) return;
        const fs = r.data.financeScope;
        if (fs && fs.mode !== "none" && fs.summary) setFinanceScopeSummary(fs.summary);
      })
      .catch(() => { /* /me failure is non-fatal — hint just stays empty */ });
    return () => { alive = false; };
  }, []);

  /* --- messages --- */
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [isThinking, setIsThinking] = useState(false);
  // Per-message feedback ratings keyed by messageId. Loaded from the
  // chat_feedback table on session select so previously-recorded thumbs
  // paint correctly when the user reopens a conversation.
  const [feedbackByMessage, setFeedbackByMessage] = useState<
    Record<string, { id: string; rating: FeedbackRating }>
  >({});
  // PR 9 — abort handle for the in-flight request (streaming or JSON fallback).
  // Reset on each new send; abort() called by the Stop button.
  const abortRef = useRef<AbortController | null>(null);
  const [activeCharts, setActiveCharts] = useState<Record<string, "bar" | "pie" | "line">>({});
  const [expandedTables, setExpandedTables] = useState<Record<string, boolean>>({});

  /* --- files --- */
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);

  /* --- thinking steps --- */
  const [thinkingStep, setThinkingStep] = useState(0);
  const thinkingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /* --- workspace --- */
  const [workspaceSessions, setWorkspaceSessions] = useState<WorkspaceSession[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  /* --- conv-bar (PR 2a) --- */
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [moreMenuTarget, setMoreMenuTarget] = useState<HTMLElement | null>(null);

  /* --- methodology drawer (PR 3b) — per-message open state --- */
  const [methodologyOpen, setMethodologyOpen] = useState<Record<string, boolean>>({});
  const [methodologySqlOpen, setMethodologySqlOpen] = useState<Record<string, boolean>>({});

  /* --- chart ↔ table toggle (PR 4c) — per-message view mode.
     true = render the polished DataTable instead of the inline chart.
     Default false: chart appears first; user can flip via the segmented
     control inside the prose box. */
  const [tableViewByMsg, setTableViewByMsg] = useState<Record<string, boolean>>({});

  /* --- workspace panel (PR 8) — accumulates chart + KPI widgets routed
     out of the chat. Opens automatically the first time a widget is
     added; closing it removes the auto-collapsed sidebar too. */
  const [workspaceWidgets, setWorkspaceWidgets] = useState<WorkspaceWidget[]>([]);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);

  /* --- dataset context (from Datasets "Ask Questions" navigation) --- */
  const [datasetContext, setDatasetContext] = useState<DatasetContextInfo | null>(null);

  // Department catalogue — loaded once, best-effort. Powers the source-
  // dataset deep-links in the methodology drawer. A failure just leaves the
  // source views as plain text (no dead links).
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  useEffect(() => {
    let cancelled = false;
    datasetService
      .getDatasets()
      .then((d) => { if (!cancelled) setDepartments(d); })
      .catch(() => { /* deep-links degrade to plain text */ });
    return () => { cancelled = true; };
  }, []);

  /* --- refs --- */
  const scrollHostRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const urlParamsHandledRef = useRef(false);

  const hasConversation = messages.length > 0;

  /* Load agents + workspace sessions */
  useEffect(() => {
    (async () => {
      try {
        const data = await agentService.getAgents();
        setAgents(data);
      } catch (e) {
        console.error("Failed to load agents:", e);
      } finally {
        setLoadingAgents(false);
      }
    })();
    setWorkspaceSessions(loadSessions());
  }, []);

  /* Handle URL params from Datasets page: ?tables=t1,t2&dept=DeptName&agentId=finance */
  useEffect(() => {
    if (urlParamsHandledRef.current) return;
    const tablesParam = searchParams.get("tables");
    const deptParam = searchParams.get("dept");
    const agentIdParam = searchParams.get("agentId");

    if (!tablesParam && !deptParam) return;

    urlParamsHandledRef.current = true;

    // Store table context with mapped agentId for seamless querying
    if (tablesParam || deptParam) {
      setDatasetContext({
        tables: tablesParam ? tablesParam.split(",").filter(Boolean) : [],
        dept: deptParam || "",
        agentId: agentIdParam || undefined,
      });
    }

    // Clear URL params to keep URL clean
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  /* Scroll handling */
  useEffect(() => {
    const host = scrollHostRef.current;
    if (!host) return;
    const onScroll = () => {
      shouldAutoScrollRef.current = isNearBottom(host);
    };
    host.addEventListener("scroll", onScroll, { passive: true });
    return () => host.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (shouldAutoScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, isThinking]);

  /* toggleChart was removed in PR 4b — the inline auto-rendered chart is
     now the single source of truth, no Bar/Pie/Line switcher. activeCharts
     state is still used (carries the auto-picked type per message). */

  /* Agent selection */
  const handleSelectAgent = useCallback((agent: Agent) => {
    if (selectedAgent?.id !== agent.id) {
      setMessages([]);
      setSessionId(undefined);
      setActiveCharts({});
      setActiveWorkspaceId(null);
    }
    setSelectedAgent(agent);
  }, [selectedAgent]);

  const handleClearAgent = useCallback(() => {
    setSelectedAgent(null);
    setMessages([]);
    setSessionId(undefined);
    setActiveCharts({});
    setAttachedFiles([]);
    setActiveWorkspaceId(null);
    setDatasetContext(null);
  }, []);

  /* File management */
  const handleFilesAdded = useCallback((files: AttachedFile[]) => {
    setAttachedFiles((prev) => [...prev, ...files]);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /* Build file context string */
  const buildFileContext = useMemo(() => {
    if (attachedFiles.length === 0) return undefined;
    return attachedFiles
      .map((f) => `--- File: ${f.name} ---\n${f.content}`)
      .join("\n\n");
  }, [attachedFiles]);

  /* Start step cycling timer */
  const startStepTimer = useCallback((totalSteps: number) => {
    setThinkingStep(0);
    if (thinkingTimerRef.current) clearInterval(thinkingTimerRef.current);
    let step = 0;
    thinkingTimerRef.current = setInterval(() => {
      step++;
      if (step < totalSteps) {
        setThinkingStep(step);
      }
    }, 1800);
  }, []);

  const stopStepTimer = useCallback(() => {
    if (thinkingTimerRef.current) {
      clearInterval(thinkingTimerRef.current);
      thinkingTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopStepTimer(), [stopStepTimer]);

  /* Persist messages to workspace.
     Accepts optional agent override so cold-start starter prompts (which
     pick a persona AND send in one click) can persist with the right agent
     before React state has caught up to the setSelectedAgent call. */
  const persistToWorkspace = useCallback((msgs: Message[], sid: string, agentOverride?: Agent | null) => {
    saveMessages(sid, msgs);
    const agent = agentOverride !== undefined ? agentOverride : selectedAgent;
    const session: WorkspaceSession = {
      id: sid,
      title: generateTitle(msgs),
      agentId: agent?.id || null,
      agentName: agent?.name || null,
      lastActivity: new Date().toISOString(),
      messageCount: msgs.length,
    };
    upsertSession(session);
    setWorkspaceSessions(loadSessions());
    setActiveWorkspaceId(sid);
  }, [selectedAgent]);

  /* Send message.
     agentOverride lets the welcome cold-start prompts (and any other caller
     that needs to set persona + send in one tick) bypass the stale-state
     problem with setSelectedAgent. When provided, it wins over UI state. */
  const handleSend = useCallback(
    async (text: string, agentOverride?: Agent | null) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      const hasDataset = !!(datasetContext && datasetContext.tables.length > 0);
      const hasFiles = attachedFiles.length > 0;

      // Resolve which agent to use for this request (local variable, not UI state)
      // Priority: explicit override > manually selected agent > dataset's mapped agent > finance fallback
      let resolvedAgent = agentOverride !== undefined ? agentOverride : selectedAgent;
      if (!resolvedAgent && hasDataset && agents.length > 0) {
        if (datasetContext?.agentId) {
          resolvedAgent = agents.find((a) => a.id === datasetContext.agentId) || null;
        }
        // Fall back to finance agent for departments without a mapped agent
        if (!resolvedAgent) {
          resolvedAgent = agents.find((a) => a.id === "finance") || null;
        }
      }
      const useAgent = !!resolvedAgent;

      if (!useAgent && !hasFiles) return;

      // If an override was provided, sync UI state so the persona pill,
      // sidebar selection, and InputBar all reflect the chosen persona.
      if (agentOverride !== undefined && agentOverride?.id !== selectedAgent?.id) {
        setSelectedAgent(agentOverride ?? null);
      }

      shouldAutoScrollRef.current = true;
      setIsThinking(true);

      // PR 9 — fresh AbortController for this turn. Stored on the ref so
      // handleStop (wired to the Stop button) can abort the active request.
      const abortController = new AbortController();
      abortRef.current = abortController;

      const steps = useAgent ? AGENT_STEPS : GENERAL_STEPS;
      startStepTimer(steps.length);

      // Ensure we have a session ID
      const currentSessionId = sessionId || `session-${Date.now()}`;
      if (!sessionId) setSessionId(currentSessionId);

      const userMsg: Message = {
        id: makeId(),
        role: "user",
        content: trimmed,
        timestamp: new Date(),
      };
      const updatedMsgs = [...messages, userMsg];
      setMessages(updatedMsgs);

      // Build context once — reused by streaming + fallback paths.
      const ctx: Record<string, any> = {};
      if (buildFileContext) ctx.fileContext = buildFileContext;
      if (datasetContext && datasetContext.tables.length > 0) {
        ctx.datasetTables = datasetContext.tables;
        ctx.datasetDept = datasetContext.dept;
      }

      // Apply a finished response to UI + storage. Single code path so the
      // streaming and non-streaming flows produce identical state.
      const applyResp = (assistantId: string, resp: any) => {
        if (resp?.sessionId) setSessionId(resp.sessionId);
        const finishedMsg: Message = {
          id: assistantId,
          role: "assistant",
          content: resp?.response ?? "(No response returned)",
          timestamp: new Date(),
          citations: resp?.citations,
          chartData: resp?.chartData ?? undefined,
          suggestions: resp?.suggestions?.filter(Boolean)?.slice(0, 4) ?? undefined,
          expandableTable: resp?.expandableTable ?? undefined,
          // Methodology trace (PR 3a/3b) — drives the "How I got this" drawer.
          trace: resp?.trace ?? undefined,
        };
        setMessages((prev) => {
          // Replace any in-flight draft assistant message with this id.
          const existing = prev.findIndex((m) => m.id === assistantId);
          const next = existing >= 0
            ? [...prev.slice(0, existing), finishedMsg, ...prev.slice(existing + 1)]
            : [...prev, finishedMsg];
          persistToWorkspace(next, resp?.sessionId || currentSessionId, resolvedAgent);
          return next;
        });
        // PR 4b — auto-pick a chart type so the chart renders on first paint.
        const autoType = pickAutoChartType(finishedMsg.chartData);
        if (autoType) {
          setActiveCharts((prev) => ({ ...prev, [assistantId]: autoType }));
        }

        // PR 8 — auto-route visual outputs (chart + KPI cards) into the
        // workspace panel. Chat keeps the prose + table; the panel
        // accumulates the visual story for the conversation.
        const cd = finishedMsg.chartData;
        if (cd) {
          const isKpi = isKpiCardResponse(cd, finishedMsg.content);
          let newWidget: WorkspaceWidget | null = null;
          if (isKpi) {
            newWidget = {
              id: `w-${assistantId}`,
              messageId: assistantId,
              caption: captionFor(trimmed, finishedMsg.content) || "Headline figures",
              kind: "kpi",
              data: cd,
            };
          } else if (autoType) {
            newWidget = {
              id: `w-${assistantId}`,
              messageId: assistantId,
              caption: captionFor(trimmed, finishedMsg.content) || "Result",
              kind: "chart",
              data: cd,
              chartType: autoType,
            };
          }
          if (newWidget) {
            setWorkspaceWidgets((prev) => [...prev, newWidget!]);
            setWorkspaceOpen(true);
            setSidebarCollapsed(true); // give the panel 50/50 with chat
          }
        }
      };

      try {
        if (useAgent) {
          // PR 6 — streaming path. Open an empty assistant draft and append
          // tokens to it as they arrive. On `complete`, replace the draft
          // with the canonical formatted response (same shape as JSON).
          const assistantId = makeId();
          let firstTokenReceived = false;

          try {
            await agentService.queryAgentStream(
              {
                agentId: resolvedAgent!.id,
                query: trimmed,
                sessionId: currentSessionId,
                context: Object.keys(ctx).length > 0 ? ctx : {},
              },
              {
                onToken: (delta: string) => {
                  if (!firstTokenReceived) {
                    firstTokenReceived = true;
                    // Drop the thinking spinner and seed the draft message.
                    setIsThinking(false);
                    setMessages((prev) => [
                      ...prev,
                      {
                        id: assistantId,
                        role: "assistant",
                        content: delta,
                        timestamp: new Date(),
                      } as Message,
                    ]);
                    return;
                  }
                  setMessages((prev) => {
                    const idx = prev.findIndex((m) => m.id === assistantId);
                    if (idx < 0) return prev;
                    const updated = { ...prev[idx], content: prev[idx].content + delta };
                    return [...prev.slice(0, idx), updated, ...prev.slice(idx + 1)];
                  });
                },
                onComplete: (payload: any) => {
                  applyResp(assistantId, payload);
                },
                onError: (msg: string) => {
                  throw new Error(msg);
                },
                signal: abortController.signal,
              },
            );
            return; // streaming success — skip the JSON fallback
          } catch (streamErr: any) {
            // PR 9 — user-initiated abort. Keep whatever was streamed,
            // append a quiet '(stopped)' marker, and skip the JSON fallback.
            if (abortController.signal.aborted) {
              setMessages((prev) => {
                const idx = prev.findIndex((m) => m.id === assistantId);
                if (idx < 0) return prev;
                const partial = prev[idx];
                const stoppedNote = "\n\n*(stopped)*";
                const updated = {
                  ...partial,
                  content: (partial.content || "") + stoppedNote,
                  timestamp: partial.timestamp ?? new Date(),
                };
                const next = [...prev.slice(0, idx), updated, ...prev.slice(idx + 1)];
                persistToWorkspace(next, currentSessionId, resolvedAgent);
                return next;
              });
              return; // don't fall through to JSON fallback after a stop
            }
            // Network blip, proxy buffering, missing endpoint, etc. — fall
            // back to the JSON path so the user still gets an answer.
            console.warn("Streaming query failed, falling back to JSON:", streamErr?.message || streamErr);
            // Drop any partial draft so the fallback's applyResp lands cleanly.
            setMessages((prev) => prev.filter((m) => m.id !== assistantId));
            setIsThinking(true);
          }
        }

        // Non-streaming path: generic LLM (chatGeneral) OR streaming fallback.
        let resp: any;
        if (useAgent) {
          resp = await agentService.queryAgent({
            agentId: resolvedAgent!.id,
            query: trimmed,
            sessionId: currentSessionId,
            context: Object.keys(ctx).length > 0 ? ctx : {},
          }, abortController.signal);
        } else {
          resp = await agentService.chatGeneral(trimmed, buildFileContext, currentSessionId);
        }
        setThinkingStep(steps.length);
        applyResp(makeId(), resp);
      } catch (e: any) {
        // PR 9 — user-aborted via Stop button. Swallow silently; the
        // streaming catch already handled the partial-draft case. The
        // JSON-fallback path lands here with an axios CanceledError or
        // a native AbortError when the controller was aborted mid-flight.
        if (abortController.signal.aborted || e?.code === "ERR_CANCELED" || e?.name === "AbortError" || e?.name === "CanceledError") {
          // no-op — message state already reflects whatever was rendered
        } else {
          const errMsg: Message = {
            id: makeId(),
            role: "system",
            content: `${e?.message || "Failed to process request."}`,
            timestamp: new Date(),
          };
          const finalMsgs = [...updatedMsgs, errMsg];
          setMessages(finalMsgs);
          persistToWorkspace(finalMsgs, currentSessionId, resolvedAgent);
        }
      } finally {
        stopStepTimer();
        setIsThinking(false);
        // Clear the abort ref if it's still ours (could have been replaced
        // by a quick subsequent send, which sets a fresh controller).
        if (abortRef.current === abortController) abortRef.current = null;
      }
    },
    [selectedAgent, agents, attachedFiles.length, messages, sessionId, buildFileContext, datasetContext, startStepTimer, stopStepTimer, persistToWorkspace]
  );

  // Drilldown — clicking a primary-dimension value in a table cell or a chart
  // bar/segment composes a scoped breakdown query and fires it through the
  // normal send path (same streaming + workspace-widget pipeline as any turn).
  // Guarded against firing mid-turn so a click can't stack requests.
  const handleDrilldown = useCallback(
    (field: string, value: string) => {
      if (isThinking) return;
      const safeValue = String(value).replace(/"/g, "'").trim();
      if (!safeValue) return;
      handleSend(`Show a detailed breakdown for ${field} "${safeValue}".`);
    },
    [handleSend, isThinking]
  );

  // PR 9 — Stop button callback. Wired through InputBar; shown only while
  // isThinking. Aborting an already-completed turn is a no-op.
  const handleStop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, []);

  // PR 8 — workspace panel callbacks
  const handleRemoveWidget = useCallback((widgetId: string) => {
    setWorkspaceWidgets((prev) => {
      const next = prev.filter((w) => w.id !== widgetId);
      // Auto-close + restore sidebar if the user emptied the panel.
      if (next.length === 0) {
        setWorkspaceOpen(false);
        setSidebarCollapsed(false);
      }
      return next;
    });
  }, []);
  const handleCloseWorkspace = useCallback(() => {
    setWorkspaceOpen(false);
    setSidebarCollapsed(false);
  }, []);
  const handleReopenWorkspace = useCallback(() => {
    setWorkspaceOpen(true);
    setSidebarCollapsed(true);
  }, []);
  // PR 8b — Save as dashboard. Snapshots the current workspace widgets
  // into a SavedDashboard via the existing /api/dashboard-builder
  // endpoints (createDashboard + updateDashboard). User picks a name;
  // success prompts for navigation to the Dashboard Builder.
  const [isSavingDashboard, setIsSavingDashboard] = useState(false);
  const handleSaveDashboard = useCallback(async () => {
    if (workspaceWidgets.length === 0 || isSavingDashboard) return;
    // Suggest a name from the first widget's caption, fall back to a
    // dated placeholder. Truncate aggressively — dashboard names render
    // in lists and shouldn't be paragraphs.
    const suggested = (workspaceWidgets[0]?.caption || "Chat workspace")
      .replace(/[?.!]+$/, "")
      .slice(0, 60);
    // eslint-disable-next-line no-alert
    const name = window.prompt("Save these widgets as a dashboard. Name:", suggested);
    if (!name || !name.trim()) return;

    setIsSavingDashboard(true);
    try {
      const widgets = workspaceToDashboardWidgets(workspaceWidgets);
      // The /api/dashboard-builder POST creates the empty dashboard;
      // PUT then attaches the widgets. Two-step is what DashboardBuilder
      // itself does — mirroring it keeps both paths consistent.
      const created = await dashboardBuilderService.createDashboard(name.trim(), []);
      await dashboardBuilderService.updateDashboard(created.id, { widgets });
      // eslint-disable-next-line no-alert
      const open = window.confirm(
        `Saved "${name.trim()}" with ${widgets.length} widget${widgets.length === 1 ? "" : "s"}.\n\nOpen it in the Dashboard Builder now?`,
      );
      if (open) {
        nav("/dashboard-builder");
      }
    } catch (err: any) {
      console.error("Failed to save dashboard:", err);
      // eslint-disable-next-line no-alert
      alert(`Couldn't save the dashboard: ${err?.message || "unknown error"}. Try again, or open the Dashboard Builder to rebuild manually.`);
    } finally {
      setIsSavingDashboard(false);
    }
  }, [workspaceWidgets, isSavingDashboard, nav]);

  /* Welcome cold-start: send a starter prompt scoped to a specific persona.
     One click sets the persona AND fires the send — the override path on
     handleSend ensures the request uses the right agent without waiting on
     React state. */
  const handleSendPromptWithAgent = useCallback((text: string, agentId: string) => {
    const target = agents.find((a) => a.id === agentId);
    if (!target) {
      // If the persona isn't available, fall back to a generic send so the
      // user still gets an answer (just without scoped data).
      handleSend(text);
      return;
    }
    handleSend(text, target);
  }, [agents, handleSend]);

  /* Workspace: select a past session */
  const handleSelectSession = useCallback((id: string) => {
    const session = workspaceSessions.find((s) => s.id === id);
    if (!session) return;
    const msgs = loadMessages(id);
    setMessages(msgs);
    setSessionId(id);
    setActiveWorkspaceId(id);
    // PR 4b — auto-pick chart types for all assistant messages with
    // chartData. Per-message toggle state isn't persisted to storage,
    // so a fresh load always shows the auto-picked default.
    const autoCharts: Record<string, "bar" | "pie" | "line"> = {};
    // PR 8 — rebuild workspace widgets from the loaded session so the
    // visual side recovers when a user revisits a conversation. We don't
    // persist widget state directly; it's derived deterministically from
    // the messages.
    const rebuiltWidgets: WorkspaceWidget[] = [];
    for (let i = 0; i < msgs.length; i++) {
      const m = msgs[i];
      if (m.role !== "assistant" || !m.chartData) continue;
      const auto = pickAutoChartType(m.chartData);
      if (auto) autoCharts[m.id] = auto;
      const userQ = i > 0 && msgs[i - 1].role === "user" ? msgs[i - 1].content : "";
      const isKpi = isKpiCardResponse(m.chartData, m.content);
      if (isKpi) {
        rebuiltWidgets.push({
          id: `w-${m.id}`,
          messageId: m.id,
          caption: captionFor(userQ, m.content) || "Headline figures",
          kind: "kpi",
          data: m.chartData,
        });
      } else if (auto) {
        rebuiltWidgets.push({
          id: `w-${m.id}`,
          messageId: m.id,
          caption: captionFor(userQ, m.content) || "Result",
          kind: "chart",
          data: m.chartData,
          chartType: auto,
        });
      }
    }
    setActiveCharts(autoCharts);
    setWorkspaceWidgets(rebuiltWidgets);
    const shouldOpen = rebuiltWidgets.length > 0;
    setWorkspaceOpen(shouldOpen);
    setSidebarCollapsed(shouldOpen);
    setAttachedFiles([]);

    // Restore agent context
    if (session.agentId) {
      const agent = agents.find((a) => a.id === session.agentId);
      if (agent) setSelectedAgent(agent);
    } else {
      setSelectedAgent(null);
    }

    // Rehydrate feedback ratings for this session so previously-recorded
    // thumbs paint correctly on each message. Fail-soft on network errors
    // so a feedback API issue never blocks a session from loading.
    setFeedbackByMessage({});
    feedbackService
      .listForSession(id)
      .then((items) => {
        const map: Record<string, { id: string; rating: FeedbackRating }> = {};
        for (const it of items) {
          map[it.messageId] = { id: it.id, rating: it.rating };
        }
        setFeedbackByMessage(map);
      })
      .catch(() => {
        /* keep an empty map; the FeedbackBar will just render in idle state */
      });
  }, [workspaceSessions, agents]);

  /* Workspace: new chat */
  const handleNewChat = useCallback(() => {
    setMessages([]);
    setSessionId(undefined);
    setActiveCharts({});
    setAttachedFiles([]);
    setActiveWorkspaceId(null);
    setFeedbackByMessage({});
    // PR 8 — drop the workspace state so the panel goes back to its
    // empty state. Sidebar comes back too since there's nothing
    // squeezing for room anymore.
    setWorkspaceWidgets([]);
    setWorkspaceOpen(false);
    setSidebarCollapsed(false);
    // Reset to the default generic LLM (no persona) — fresh-start semantics.
    // User explicitly asked: New chat returns to the default model.
    setSelectedAgent(null);
  }, []);

  /* Workspace: delete session */
  const handleDeleteSession = useCallback((id: string) => {
    deleteSessionStorage(id);
    setWorkspaceSessions(loadSessions());
    if (activeWorkspaceId === id) {
      handleNewChat();
    }
  }, [activeWorkspaceId, handleNewChat]);

  /* Workspace: toggle pinned (PR 5b).
     Persists via the existing upsertSession so the flag survives reload. */
  const handleTogglePinSession = useCallback((id: string, pinned: boolean) => {
    setWorkspaceSessions((prev) => {
      const session = prev.find((s) => s.id === id);
      if (!session) return prev;
      upsertSession({ ...session, pinned });
      return loadSessions();
    });
  }, []);

  /* PR 2a — conv-bar helpers */
  const currentSession = workspaceSessions.find((s) => s.id === activeWorkspaceId) || null;
  const currentTitle = currentSession?.title || (messages.length ? generateTitle(messages) : "");

  /* Commit a renamed conversation title. No-op if empty/unchanged. */
  const commitTitleRename = useCallback(
    (raw: string) => {
      setIsEditingTitle(false);
      const trimmed = raw.trim();
      if (!currentSession) return;
      if (!trimmed) return; // empty input — keep old title
      if (trimmed === currentSession.title) return;
      upsertSession({ ...currentSession, title: trimmed });
      setWorkspaceSessions(loadSessions());
    },
    [currentSession]
  );

  const handleDeleteCurrent = useCallback(() => {
    setMoreMenuTarget(null);
    if (!activeWorkspaceId) return;
    handleDeleteSession(activeWorkspaceId);
  }, [activeWorkspaceId, handleDeleteSession]);

  const handleRenameFromMenu = useCallback(() => {
    setMoreMenuTarget(null);
    setIsEditingTitle(true);
  }, []);

  /* Render citations — SQL citations are expandable */
  /* PR 4a — render single-row summary responses as a KPI card grid.
     Negative currency values get a red treatment for at-a-glance variance. */
  const renderKpiCards = (chartData: any): React.ReactNode => {
    if (!chartData || !chartData.rows?.[0] || !chartData.fields) return null;
    const row = chartData.rows[0];
    return (
      <div className={styles.kpiRow}>
        {chartData.fields.map((field: { name: string }) => {
          const raw = row[field.name];
          const parsed = parseNumericValue(raw);
          const isNegative = parsed ? parsed.num < 0 : false;
          // Variance / margin / change columns get the red/green treatment.
          // Plain actuals/budgets stay neutral.
          const isVarianceField = /variance|change|delta|gain|loss/i.test(field.name);
          const valueClass = isVarianceField && isNegative
            ? `${styles.kpiValue} ${styles.kpiValueBad}`
            : isVarianceField && parsed && parsed.num > 0
              ? `${styles.kpiValue} ${styles.kpiValueGood}`
              : styles.kpiValue;
          return (
            <div key={field.name} className={styles.kpiCard}>
              <div className={styles.kpiLabel}>{formatColumnHeader(field.name)}</div>
              <div className={valueClass}>{formatKpiValue(raw)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // Trust signals (PR — trust cluster).
  // Classification badge in the conv-bar, driven by the agent's authoritative
  // `dataClassification` marking. OFFICIAL reads green (public); OFFICIAL:
  // Sensitive / PROTECTED read amber (governed). `tier` is intentionally NOT
  // used — it's an optional field that is never populated.
  const classification = useMemo(() => {
    const marking = selectedAgent?.dataClassification;
    if (!marking) return null;
    const isPublic = marking === "OFFICIAL";
    return {
      variant: isPublic ? ("public" as const) : ("governed" as const),
      label: isPublic ? "OFFICIAL · Public" : `${marking} · Governed`,
    };
  }, [selectedAgent]);

  // The dataset behind the active agent — used to deep-link source views in
  // the methodology drawer. Only resolves to a dataset the user can access,
  // so links never dead-end on a forbidden page.
  const sourceDataset = useMemo(() => {
    const agentId = selectedAgent?.id || datasetContext?.agentId;
    if (!agentId) return null;
    return departments.find((d) => d.agentId === agentId && d.hasAccess) || null;
  }, [departments, selectedAgent, datasetContext]);

  /* PR 3b — render methodology trace as a "How I got this" expandable.
     Returns null when there are no steps (e.g. cached errors, ABS public
     responses with no trace, very old messages from before PR 3a landed).

     Uses an open-state hook (useState below) so the chevron rotates and the
     body shows/hides via React rather than the native <details> toggle —
     gives us reliable styling across browsers. */
  const renderMethodology = (
    msgId: string,
    trace: AgentTrace | undefined
  ): React.ReactNode => {
    if (!trace || !Array.isArray(trace.steps) || trace.steps.length === 0) return null;

    const retryCount = trace.steps.filter((s) => s.kind === "retry" || s.retried).length;
    const stepsLabel = `${trace.steps.length} step${trace.steps.length === 1 ? "" : "s"}`;
    const summaryPillText = retryCount > 0 ? `${stepsLabel} · ${retryCount} retry` : stepsLabel;
    const totalLabel = trace.totalMs >= 1000
      ? `${(trace.totalMs / 1000).toFixed(1)}s`
      : `${trace.totalMs} ms`;

    const fmtDuration = (ms: number) =>
      ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms} ms`;

    const fmtRows = (n: number) => n.toLocaleString("en-AU");

    const isOpen = !!methodologyOpen[msgId];
    const isSqlOpen = !!methodologySqlOpen[msgId];

    const renderStep = (step: AgentTraceStep, idx: number) => {
      const isRetry = step.kind === "retry" || step.retried === true;
      return (
        <div
          key={idx}
          className={`${styles.methodologyStep} ${isRetry ? styles.methodologyStepRetry : ""}`}
        >
          <div className={styles.methodologyStepN}>{String(idx + 1).padStart(2, "0")}</div>
          <div className={styles.methodologyStepWhat}>
            {isRetry && <span className={styles.methodologyRetryMark}>↻</span>}
            <span className={styles.methodologyStepLabel}>{step.label}</span>
            {step.model && (
              <span className={styles.methodologyStepEm}>· {step.model}</span>
            )}
            {step.tokensUsed ? (
              <span className={styles.methodologyStepEm}>
                · {fmtRows(step.tokensUsed)} tokens
              </span>
            ) : null}
            {step.views && step.views.length > 0 && (
              <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
                {step.views.map((v) =>
                  sourceDataset ? (
                    <button
                      key={v}
                      type="button"
                      className={styles.methodologyStepCodeLink}
                      title={`View source dataset: ${sourceDataset.department}`}
                      onClick={() => nav(`/datasets?dept=${encodeURIComponent(sourceDataset.id)}`)}
                    >
                      {v}
                      <span className={`${styles.methodologyStepCodeArrow} ds-link-arrow`} aria-hidden="true">↗</span>
                    </button>
                  ) : (
                    <code key={v} className={styles.methodologyStepCode}>{v}</code>
                  )
                )}
              </span>
            )}
            {step.error ? (
              <span className={styles.methodologyStepError}>— {step.error}</span>
            ) : null}
            {typeof step.rows === "number" && (
              <span className={styles.methodologyStepBadge}>{fmtRows(step.rows)} rows</span>
            )}
            <span
              className={styles.methodologyStepBadge}
              style={{ marginLeft: "auto" }}
            >{fmtDuration(step.durationMs)}</span>
          </div>
        </div>
      );
    };

    const setOpen = (v: boolean) =>
      setMethodologyOpen((prev) => ({ ...prev, [msgId]: v }));
    const setSqlOpen = (v: boolean) =>
      setMethodologySqlOpen((prev) => ({ ...prev, [msgId]: v }));

    return (
      <div className={styles.methodology}>
        <div
          className={styles.methodologySummary}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          onClick={() => setOpen(!isOpen)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen(!isOpen);
            }
          }}
        >
          <div className={styles.methodologySummaryRow}>
            <span className={`${styles.methodologyChevron} ${isOpen ? styles.methodologyChevronOpen : ""}`}>▸</span>
            <span className={styles.methodologyHeadLabel}>How I got this</span>
            <span className={styles.methodologyHeadMeta}>
              <span className={`${styles.methodologyPill} ${retryCount > 0 ? styles.methodologyPillWarn : ""}`}>
                {summaryPillText}
              </span>
              <span>{totalLabel}</span>
            </span>
          </div>
        </div>
        {isOpen && (
          <div className={styles.methodologyBody}>
            {trace.steps.map(renderStep)}
            {trace.generatedSql && (
              <div className={styles.methodologySql}>
                <div
                  className={styles.methodologySqlSummary}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isSqlOpen}
                  onClick={() => setSqlOpen(!isSqlOpen)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSqlOpen(!isSqlOpen);
                    }
                  }}
                >
                  <div className={styles.methodologySqlSummaryRow}>
                    <span style={{ fontSize: 9, color: "#747474", transition: "transform 0.15s", transform: isSqlOpen ? "rotate(90deg)" : "none", display: "inline-block" }}>▸</span>
                    <span>{isSqlOpen ? "Hide" : "View"} generated SQL</span>
                  </div>
                </div>
                {isSqlOpen && (
                  <pre className={styles.methodologySqlBody}><code>{trace.generatedSql}</code></pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCitations = (raw: any, hideSql: boolean = false) => {
    if (!raw) return null;
    const arr = Array.isArray(raw) ? raw : [raw];
    if (!arr.length) return null;

    const sqlCitations = hideSql
      ? []
      : arr.filter((c: any) => c?.id === "sql-query" && c?.excerpt);
    const otherCitations = normalizeCitations(arr);

    if (sqlCitations.length === 0 && otherCitations.length === 0) return null;

    return (
      <div className={styles.citationsWrap}>
        {sqlCitations.map((c: any, idx: number) => (
          <details key={`sql-${idx}`} className="sql-citation-details">
            <summary className="sql-citation-summary">
              <Code size={12} weight="regular" style={{ opacity: 0.6 }} />
              Generated SQL
            </summary>
            <div className="md-code-block" style={{ margin: 0, borderRadius: 0, borderTop: "1px solid #EDEDED" }}>
              <pre><code>{c.excerpt}</code></pre>
            </div>
          </details>
        ))}

        {otherCitations.length > 0 && (
          <div style={{ marginTop: sqlCitations.length > 0 ? 8 : 0 }}>
            <div className={styles.citationsTitle}>Sources</div>
            <div>
              {otherCitations.slice(0, 6).map((c, idx) => (
                <span key={`${c.label}-${idx}`} className={styles.citationChip}>
                  <Database size={11} weight="regular" style={{ opacity: 0.6 }} />
                  {c.url ? (
                    <a href={c.url} target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "none" }}>
                      {c.label}
                    </a>
                  ) : (
                    c.label
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  /* Loading state */
  if (loadingAgents) {
    return (
      <div className={styles.loadingPage}>
        <Spinner size={SpinnerSize.large} label="Loading..." />
      </div>
    );
  }

  /* ========== Render ========== */
  return (
    <div className={styles.pageRow}>
      {/* Workspace sidebar — collapsible. Carries personas + recent.
          Persona selection mirrors agent selection (one source of truth). */}
      {!sidebarCollapsed && (
        <WorkspaceSidebar
          sessions={workspaceSessions}
          activeSessionId={activeWorkspaceId}
          onSelectSession={handleSelectSession}
          onNewChat={handleNewChat}
          onDeleteSession={handleDeleteSession}
          onTogglePinSession={handleTogglePinSession}
          agents={agents}
          selectedAgent={selectedAgent}
          onSelectAgent={handleSelectAgent}
          // Tier resolver — agent.tier comes from agentService.enrichAgentsWithTier
          getAgentTier={(id) => {
            const a = agents.find((x) => x.id === id) as (Agent & { tier?: "public" | "governed" }) | undefined;
            if (a?.tier === "public") return "public";
            return "governed";
          }}
          // Only Finance honours the user's finance hierarchy scope today
          agentHonorsHierarchy={(id) => id === "finance"}
        />
      )}

      {/* Chat area — splits 50/50 with the workspace when it's open. */}
      <div className={`${styles.chatArea} ${workspaceOpen ? styles.chatAreaSplit : ""}`}>
        {/* PR 2a — Conversation bar.
            Welcome state: just the sidebar toggle.
            Persona picked (no messages yet): toggle + persona chip + New chat.
            Conversation active: toggle + editable title + persona+scope sub-line +
              New chat + ⋯ menu (Rename / Delete). */}
        <div className={styles.convBar}>
          <div className={styles.convBarLeft}>
            <button
              type="button"
              className={styles.convBarSidebarToggle}
              onClick={() => setSidebarCollapsed((p) => !p)}
              title={sidebarCollapsed ? "Show workspace" : "Hide workspace"}
            >
              {sidebarCollapsed ? <CaretRight size={12} weight="bold" /> : <CaretLeft size={12} weight="bold" />}
              {sidebarCollapsed ? "Workspace" : ""}
            </button>

            {hasConversation ? (
              <div className={styles.convBarTitleBlock}>
                {isEditingTitle ? (
                  <input
                    autoFocus
                    type="text"
                    className={styles.convBarTitleInput}
                    defaultValue={currentTitle}
                    onBlur={(e) => commitTitleRename(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        setIsEditingTitle(false);
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className={styles.convBarTitle}
                    onClick={() => setIsEditingTitle(true)}
                    title="Click to rename"
                  >
                    {currentTitle || "New chat"}
                  </button>
                )}
                {selectedAgent && (
                  <div className={styles.convBarSubLine}>
                    <span className={styles.convBarPersonaChip}>
                      <span className={styles.convBarPersonaDot} />
                      {selectedAgent.name}
                    </span>
                    {selectedAgent.id === "finance" && financeScopeSummary && (
                      <span className={styles.convBarScope}>{financeScopeSummary}</span>
                    )}
                    {classification && (
                      <span
                        className={classification.variant === "public" ? styles.convBarClassPublic : styles.convBarClassGov}
                        title="Data classification of this dataset"
                      >
                        {classification.variant === "public"
                          ? <ShieldCheck size={11} weight="fill" />
                          : <Lock size={11} weight="fill" />}
                        {classification.label}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : selectedAgent ? (
              <div className={styles.convBarSubLine}>
                <span className={styles.convBarPersonaChip}>
                  <span className={styles.convBarPersonaDot} />
                  {selectedAgent.name}
                </span>
                {selectedAgent.id === "finance" && financeScopeSummary && (
                  <span className={styles.convBarScope}>{financeScopeSummary}</span>
                )}
                {classification && (
                  <span
                    className={classification.variant === "public" ? styles.convBarClassPublic : styles.convBarClassGov}
                    title="Data classification of this dataset"
                  >
                    {classification.variant === "public"
                      ? <ShieldCheck size={11} weight="fill" />
                      : <Lock size={11} weight="fill" />}
                    {classification.label}
                  </span>
                )}
              </div>
            ) : null}
          </div>

          <div className={styles.convBarRight}>
            {/* PR 8 — re-open workspace when it's been closed but widgets
                remain. Shows the current widget count for at-a-glance
                "there's stuff in the panel" affordance. */}
            {!workspaceOpen && workspaceWidgets.length > 0 && (
              <button
                type="button"
                className={styles.convBarPrimaryBtn}
                onClick={handleReopenWorkspace}
                title="Show workspace panel"
              >
                <ChartBar size={12} weight="regular" />
                Workspace ({workspaceWidgets.length})
              </button>
            )}
            {(hasConversation || selectedAgent) && (
              <button
                type="button"
                className={styles.convBarPrimaryBtn}
                onClick={handleNewChat}
                title="Start a fresh conversation in the default model"
              >
                <Broom size={12} weight="regular" />
                New chat
              </button>
            )}
            {hasConversation && (
              <button
                type="button"
                className={styles.convBarIconBtn}
                onClick={(e) => setMoreMenuTarget(e.currentTarget)}
                title="More actions"
                aria-label="More actions"
              >
                <DotsThreeVertical size={16} weight="bold" />
              </button>
            )}
          </div>

          {moreMenuTarget && (
            <Callout
              target={moreMenuTarget}
              isBeakVisible={false}
              directionalHint={DirectionalHint.bottomRightEdge}
              onDismiss={() => setMoreMenuTarget(null)}
              calloutWidth={200}
              styles={{
                root: {
                  background: "#FFFFFF",
                  border: "1px solid #E1E1E1",
                  borderRadius: 6,
                  overflow: "hidden",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.10)",
                },
              }}
            >
              <div
                className={styles.convBarMenuItem}
                onClick={handleRenameFromMenu}
              >
                <PencilSimple size={14} weight="regular" />
                Rename
              </div>
              <div
                className={`${styles.convBarMenuItem} ${styles.convBarMenuItemDanger}`}
                onClick={handleDeleteCurrent}
              >
                <Trash size={14} weight="regular" />
                Delete chat
              </div>
            </Callout>
          )}
        </div>
        {/* Access banner removed 2026-05-07 — its info is now woven into the
            WelcomeScreen's hint line + WorkspaceSidebar's persona selection. */}
        {/* Scrollable messages area */}
        <div className={styles.messagesScroll} ref={scrollHostRef}>
          <div className={styles.messagesInner}>
            {/* Welcome screen (no messages) */}
            {!hasConversation && (
              <WelcomeScreen
                agents={agents}
                selectedAgent={selectedAgent}
                onSelectAgent={handleSelectAgent}
                onSendPrompt={handleSend}
                onSendPromptWithAgent={handleSendPromptWithAgent}
                isThinking={isThinking}
                agentHonorsHierarchy={(id) => id === "finance"}
                financeScopeSummary={financeScopeSummary}
                getAgentTier={(id) => {
                  const a = agents.find((x) => x.id === id) as (Agent & { tier?: "public" | "governed" }) | undefined;
                  if (a?.tier === "public") return "public";
                  return "governed";
                }}
              />
            )}

            {/* Message list */}
            {messages.map((m: any, idx: number) => {
              const isUser = m.role === "user";
              const isAssistant = m.role === "assistant";
              const isSystem = m.role === "system";

              if (isUser) {
                return (
                  <div key={m.id} className={`${styles.messageRow} ${styles.messageRowUser}`}>
                    {/* PR 7 — user initials on brand-tint circle */}
                    <div
                      className={`${styles.avatar} ${styles.avatarUser}`}
                      title={user?.name || user?.email || "You"}
                      aria-label={user?.name || "You"}
                    >
                      {userInitials}
                    </div>
                    <div className={styles.messageContent}>
                      <div className={styles.userBubble}>{m.content}</div>
                      <span className={`${styles.timeText} ${styles.timeRight}`}>
                        {formatTime(m.timestamp)}
                      </span>
                    </div>
                  </div>
                );
              }

              if (isAssistant) {
                // PR 4a — single-row summary → KPI cards.
                // PR 4b — multi-row response with chart-friendly shape →
                // auto-rendered chart (with chart↔table toggle from 4c).
                // PR 7 — multi-row response that DOESN'T chart still uses
                // the polished DataTable component, so the table styling
                // is consistent whether you arrived via the chart toggle
                // or directly. The raw markdown table renderer is now a
                // last-resort fallback (no chartData at all).
                // PR 8 — when a workspace widget owns the visual for this
                // message, suppress the inline chart / KPI cards and let
                // the DataTable handle the in-chat data view instead.
                const widgetForMsg = workspaceWidgets.find((w) => w.messageId === m.id);
                const hasWidgetKpi = widgetForMsg?.kind === "kpi";
                const hasWidgetChart = widgetForMsg?.kind === "chart";

                const hasKpiCards = !hasWidgetKpi && isKpiCardResponse(m.chartData, m.content);
                const inlineChartType = !hasKpiCards && !hasWidgetChart ? activeCharts[m.id] : undefined;
                const hasInlineChart = !!inlineChartType && !!m.chartData;
                const useDataTableFallback =
                  !hasKpiCards &&
                  !hasInlineChart &&
                  !!m.chartData &&
                  (m.chartData.rowCount ?? m.chartData.rows?.length ?? 0) >= 2 &&
                  containsMarkdownTable(m.content);
                const shouldStripTable =
                  hasWidgetKpi ||
                  hasWidgetChart ||
                  hasKpiCards ||
                  hasInlineChart ||
                  useDataTableFallback;
                // PR 7 — persona-aware assistant avatar (initial on persona gradient)
                const personaVis = personaVisual(selectedAgent);
                return (
                  <div key={m.id} className={`${styles.messageRow} assistant-message-row`}>
                    <div
                      className={`${styles.avatar} ${styles.avatarAssistant}`}
                      style={{ background: personaVis.gradient }}
                      title={selectedAgent?.name || "General"}
                      aria-label={`${selectedAgent?.name || "General"} response`}
                    >
                      {personaVis.initial}
                    </div>
                    <div className={styles.messageContent}>
                      <div className={`${styles.assistantProse} assistant-prose-wrap`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
                          {(() => {
                            // KPI cards OR inline chart: strip the count line + markdown table block.
                            if (shouldStripTable) return stripCountAndTableFromMarkdown(m.content);
                            if (!expandedTables[m.id] || !m.expandableTable) return m.content;
                            // Replace the preview table (first markdown table block) with the full table
                            const tableStart = m.content.indexOf("| ");
                            if (tableStart < 0) return m.content;
                            const beforeTable = m.content.slice(0, tableStart);
                            // Find end of table block (double newline or end of string)
                            const afterTableMatch = m.content.slice(tableStart).match(/\n\n(?!\|)/);
                            const afterTable = afterTableMatch
                              ? m.content.slice(tableStart + (afterTableMatch.index ?? 0))
                              : "";
                            return beforeTable + m.expandableTable + afterTable;
                          })()}
                        </ReactMarkdown>
                        {/* PR 4b inline chart + PR 4c chart ↔ table toggle.
                            embedded mode strips ChartPanel's own card chrome
                            so it inherits the prose box's white background.
                            The segmented control is right-aligned above the
                            visualisation so the chart / table itself remains
                            the primary thing in view. */}
                        {hasInlineChart && (() => {
                          const showTable = !!tableViewByMsg[m.id];
                          return (
                            <>
                              <div className={styles.viewSwitcherWrap}>
                                <div className={styles.viewSwitcher} role="tablist" aria-label="View as chart or table">
                                  <button
                                    type="button"
                                    role="tab"
                                    aria-selected={!showTable}
                                    className={`${styles.viewSwitcherBtn} ${!showTable ? styles.viewSwitcherBtnActive : ""}`}
                                    onClick={() => setTableViewByMsg((prev) => ({ ...prev, [m.id]: false }))}
                                    title="Show as chart"
                                  >
                                    <ChartBar size={13} weight="regular" />
                                    Chart
                                  </button>
                                  <button
                                    type="button"
                                    role="tab"
                                    aria-selected={showTable}
                                    className={`${styles.viewSwitcherBtn} ${showTable ? styles.viewSwitcherBtnActive : ""}`}
                                    onClick={() => setTableViewByMsg((prev) => ({ ...prev, [m.id]: true }))}
                                    title="Show as table"
                                  >
                                    <TableIcon size={13} weight="regular" />
                                    Table
                                  </button>
                                </div>
                              </div>
                              {showTable ? (
                                <DataTable data={m.chartData!} onDrilldown={handleDrilldown} />
                              ) : (
                                <ChartPanel
                                  data={m.chartData!}
                                  chartType={inlineChartType!}
                                  embedded
                                  height={280}
                                  onDrilldown={handleDrilldown}
                                />
                              )}
                            </>
                          );
                        })()}
                      </div>
                      {/* PR 4a — KPI card grid, replaces the 1-row markdown table */}
                      {hasKpiCards && renderKpiCards(m.chartData)}
                      {/* PR 7 — DataTable fallback for multi-row responses
                          that don't auto-chart. Same component used by the
                          chart↔table toggle (PR 4c), so the visual is
                          consistent across both paths. */}
                      {useDataTableFallback && (
                        <DataTable data={m.chartData!} onDrilldown={handleDrilldown} />
                      )}
                      {/* 'Show all rows' is only meaningful when the markdown
                          table itself is on screen. If we've stripped it for a
                          chart or KPI cards, the button doesn't apply. */}
                      {m.expandableTable && !shouldStripTable && (
                        <button
                          className="chat-turn-action"
                          onClick={() => setExpandedTables((prev) => ({ ...prev, [m.id]: !prev[m.id] }))}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "5px 12px",
                            borderRadius: 6,
                            border: "1px solid #E1E1E1",
                            background: "#fff",
                            color: "#2e873d",
                            fontSize: 12,
                            fontWeight: 500,
                            cursor: "pointer",
                            marginTop: 6,
                            transition: "all 0.15s",
                          }}
                        >
                          {expandedTables[m.id] ? "Show less" : "Show all rows (up to 100)"}
                        </button>
                      )}
                      {/* Export to Excel is the only action below the prose
                          now — Bar/Pie/Line switcher removed per user
                          direction ('we don't need 3 options every time'). */}
                      {(m.chartData || containsMarkdownTable(m.content)) && (
                        <div className="chat-turn-action" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                          <button
                            className={styles.exportBtn}
                            style={{ marginTop: 0 }}
                            onClick={() =>
                              // Prefer the full chartData rows (uncapped). Markdown content
                              // is the fallback — it only carries the first ~10 preview rows.
                              exportTableToExcel(
                                m.chartData ?? m.content,
                                `export-${new Date().toISOString().slice(0, 10)}.xlsx`
                              )
                            }
                            title={
                              m.chartData
                                ? `Export underlying data to Excel (${m.chartData.rowCount} rows)`
                                : "Export underlying data to Excel"
                            }
                          >
                            <MicrosoftExcelLogo size={14} weight="regular" />
                            Export to Excel
                          </button>
                        </div>
                      )}
                      {/* Methodology drawer (PR 3b) — sits between prose and
                          citations. Renders only when m.trace has steps (i.e.
                          governed agents post-3a; older messages keep the
                          legacy 'Generated SQL' chip via renderCitations). */}
                      {renderMethodology(m.id, m.trace)}
                      {renderCitations(m.citations, !!(m.trace && m.trace.steps && m.trace.steps.length > 0))}
                      {/* Follow-up suggestion chips (PR 2c) — only on the last
                          assistant message, suppressed while a new turn is in
                          flight. Heading frames them as suggestions; arrow
                          prefix turns brand-green on hover. */}
                      {m.suggestions && m.suggestions.length > 0 && m.id === messages.filter((x: any) => x.role === "assistant").slice(-1)[0]?.id && !isThinking && (
                        <div className={styles.followupsBlock}>
                          <div className={styles.followupsHeading}>Suggested follow-ups</div>
                          <div className={styles.followupsRow}>
                            {m.suggestions.map((s: string, sidx: number) => (
                              <button
                                key={sidx}
                                type="button"
                                className={styles.followupChip}
                                onClick={() => handleSend(s)}
                                disabled={isThinking}
                              >
                                <span
                                  className={`${styles.followupArrow} followup-arrow`}
                                  aria-hidden="true"
                                >→</span>
                                {s}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Feedback bar — subtle thumbs below each assistant
                          response. Skipped while streaming (no stable
                          message yet) and while sessionId is unset (we need
                          it to anchor the rating row). */}
                      {!isThinking && sessionId && (
                        <FeedbackBar
                          agentId={selectedAgent?.id || "general"}
                          sessionId={sessionId}
                          messageId={m.id}
                          extras={{
                            query:
                              idx > 0 && messages[idx - 1]?.role === "user"
                                ? messages[idx - 1].content
                                : null,
                            response: m.content,
                            sqlText: m.trace?.generatedSql ?? null,
                            rowCount: m.chartData?.rowCount ?? null,
                            trace: m.trace,
                            responseTimeMs: m.trace?.totalMs ?? null,
                          }}
                          initialRating={feedbackByMessage[m.id]?.rating ?? null}
                          initialId={feedbackByMessage[m.id]?.id ?? null}
                        />
                      )}
                      <span className={styles.timeText}>{formatTime(m.timestamp)}</span>
                    </div>
                  </div>
                );
              }

              if (isSystem) {
                return (
                  <div key={m.id} className={styles.messageRow}>
                    <div className={styles.avatar} style={{ background: "#FFF8E1", color: "#8D6E00" }}>
                      <Warning size={15} weight="fill" />
                    </div>
                    <div className={styles.messageContent}>
                      <div className={styles.systemBubble}>{m.content}</div>
                      <span className={styles.timeText}>{formatTime(m.timestamp)}</span>
                    </div>
                  </div>
                );
              }

              return null;
            })}

            {/* Thinking — compact single-line rotating status */}
            {isThinking && (() => {
              const steps = selectedAgent ? AGENT_STEPS : GENERAL_STEPS;
              const currentLabel = steps[Math.min(thinkingStep, steps.length - 1)];
              const thinkVis = personaVisual(selectedAgent);
              return (
                <div className={styles.thinkingRow}>
                  <div
                    className={`${styles.avatar} ${styles.avatarAssistant}`}
                    style={{ background: thinkVis.gradient }}
                    aria-label={`${selectedAgent?.name || "General"} thinking`}
                  >
                    {thinkVis.initial}
                  </div>
                  <div className={styles.thinkingBubble} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px" }}>
                    <Spinner size={SpinnerSize.xSmall} />
                    <span style={{ fontSize: 13, color: "#555", fontWeight: 500, transition: "opacity 0.3s" }}>{currentLabel}</span>
                  </div>
                </div>
              );
            })()}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input bar — pinned to bottom in conversation state. In welcome
            (no messages) WelcomeScreen renders its own centered compose
            pill, so the bottom InputBar is hidden to avoid two inputs on
            the page at once. */}
        {hasConversation && (
          <InputBar
            agents={agents}
            selectedAgent={selectedAgent}
            attachedFiles={attachedFiles}
            datasetContext={datasetContext}
            isThinking={isThinking}
            onSend={handleSend}
            onStop={handleStop}
            onSelectAgent={handleSelectAgent}
            onClearAgent={handleClearAgent}
            onFilesAdded={handleFilesAdded}
            onRemoveFile={handleRemoveFile}
            onClearDataset={() => setDatasetContext(null)}
          />
        )}
      </div>

      {/* PR 8 — Workspace panel. Sibling of the chat area; flexes to
          50/50 when open. Sidebar auto-collapses when this opens. */}
      <div
        className={`${styles.workspaceArea} ${workspaceOpen ? styles.workspaceAreaOpen : styles.workspaceAreaClosed}`}
        aria-hidden={!workspaceOpen}
      >
        {workspaceOpen && (
          <WorkspacePanel
            widgets={workspaceWidgets}
            onRemoveWidget={handleRemoveWidget}
            onClose={handleCloseWorkspace}
            onSave={handleSaveDashboard}
            onDrilldown={handleDrilldown}
          />
        )}
      </div>
    </div>
  );
}
